"use client";

import { isOpenInApp, stringToBoolean } from "@repo/shared-utils/pwa-utils";
import {
  selectIsAuthSuccessOrPerpetual,
  selectLeadStatus,
  setIsCommonSsoModalVisible,
  store,
  useAppSelector,
} from "@repo/web-store";
import { usePathname, useSearchParams } from "next/navigation";
import { FC, useCallback, useMemo, useState } from "react";
import { handleRedirection } from "../../utils/helper";
import {
  API_ERROR_DESC,
  textEllipsis,
  TITLE_CHAR_LIMIT,
} from "../ClpStockCards/clpCardConstant";
import StockListNotFound from "../ClpStockCards/StockListNotFound";
import { PortfolioHoldingsComponent } from "./PortfolioHoldings";
import {
  CmpDataPortfolioProps,
  HoldingsResponse,
  InvestmentDatum,
  PortfolioSummary,
} from "./PortfolioTypes";
import AuthenticatePortfolio from "./AuthenticatePortfolio/AuthenticatePortfolio";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import {
  componentNameConstants,
  dlElementConstants,
  dlEventConstants,
} from "@repo/analytics/data-layer.constants";
import { getSessionData } from "@repo/shared-utils/common-utils";

interface PortfolioLockedCardProps {
  cmpData: CmpDataPortfolioProps;
  portfolioData: PortfolioSummary;

  holdings?: HoldingsResponse | null;
  hasDemat: boolean;
  hasPortfolio: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
}

const DEFAULT_SUMMARY: PortfolioSummary = {
  totalCurrentValue: "0",
  totalInvestedValue: "0",
  totalPL: "0",
  totalPLPercentage: "0.00",
  total1DayPL: "0",
  total1DayPLPercentage: "0.00",
  plStatus: "neutral",
  oneDayPLStatus: "neutral",
};

const PortfolioLockedCard: FC<PortfolioLockedCardProps> = ({
  cmpData,
  portfolioData,
  holdings,
  hasDemat,
  hasPortfolio,
  onLoadMore,
  isLoadingMore = false,
}) => {
  const leadStatus = useAppSelector(selectLeadStatus);
  const isAuthSuccessOrPerpetual = useAppSelector(
    selectIsAuthSuccessOrPerpetual,
  );
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const code = searchParams.get("code") ?? "";

  const shouldRenderHoldings =
    hasPortfolio &&
    pathname === "/portfolio-dashboard" &&
    isAuthSuccessOrPerpetual;

  const [portfolioVisible, setPortfolioVisible] = useState(true);

  const formatValue = useCallback(
    (value: string) => {
      if (!portfolioVisible) return "******";
      const numeric = Number(value ?? 0);
      const display = Number.isNaN(numeric)
        ? value
        : Math.abs(numeric).toString();
      return `${display}`;
    },
    [portfolioVisible],
  );

  const formatPercentage = useCallback(
    (percentage: string) => {
      if (!portfolioVisible) return " (**)";
      return ` (${percentage ?? "0"}%)`;
    },
    [portfolioVisible],
  );

  // Get investment data for display
  const getInvestmentData = useCallback((): InvestmentDatum[] => {
    const summary = portfolioData ?? DEFAULT_SUMMARY;
    return [
      {
        label: cmpData?.currentValueText ?? "Current Value",
        value: (code === "" || !code) ? "******" : summary.totalCurrentValue ?? "" ,
      },
      {
        label: cmpData?.todayPLText ?? "Total P&L",
        value: (code === "" || !code) ? "******" : `${formatValue(summary.totalPL)} ${formatPercentage(summary.totalPLPercentage)}`,
        totalPL: summary.totalPL,
        totalPLPercentage: summary.totalPLPercentage,
      },
      {
        label: cmpData?.investedText ?? "Invested",
        value: (code === "" || !code) ? "******" : summary.totalInvestedValue ?? "",
      },
      {
        label: cmpData?.oneDayPLText ?? "1 Day P&L",
        value: (code === "" || !code) ? "******" : `${formatValue(summary.total1DayPL)} ${formatPercentage(summary.total1DayPLPercentage)}`,
        totalPL: summary.total1DayPL,
        totalPLPercentage: summary.total1DayPLPercentage,
      },
    ];
  }, [cmpData, formatPercentage, formatValue, portfolioData]);

  // Handle redirection URL
  const handleRedirectionUrl = useCallback(() => {
    const baseUrl = isOpenInApp()
      ? (cmpData.urls?.viewAllRedirectUrlApp ?? "/app-v1/portfolio-dashboard")
      : (cmpData.urls?.viewAllRedirectUrlWeb ?? "/portfolio-dashboard");

    const currentQueryParams = window.location.search;
    const url = baseUrl + currentQueryParams;
    const redirectNewTab = cmpData.loggedOutRedirectNewTab ? "_blank" : "_self";

    dataLayer(
      "",
      {
        section_title: "Portfolio",
        event: dlEventConstants.CLICK,
        element: dlEventConstants.LINK_CLICK,
        cta_text: cmpData?.viewPortfolioText ?? "View Portfolio",
        event_type: dlEventConstants.LINK_CLICK,
        destination_url: url,
      },
      {
        EP_PAGE_NAME: componentNameConstants.PORTFOLIO_NAME,
        EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
        EP_CTA: cmpData?.viewPortfolioText ?? "View Portfolio",
      },
    );

    window.open(url, redirectNewTab);
  }, [
    cmpData.loggedOutRedirectNewTab,
    cmpData.urls?.viewAllRedirectUrlApp,
    cmpData.urls?.viewAllRedirectUrlWeb,
    cmpData?.viewPortfolioText,
  ]);

  // Get error text based on state
  const handleErrorText = useCallback(() => {
    if (!hasDemat) {
      const baseUrl = isOpenInApp()
        ? cmpData.loggedInOpenDematUrlApp
        : cmpData.loggedInOpenDematUrlWeb;
      const redirectNewTab =
        cmpData.loggedInOpenDematRedirectNewTab === "true" ? "_blank" : "_self";

      return {
        errorMessage:
          cmpData.messages?.loggedInOpenDematText ??
          cmpData.loggedInOpenDematText ??
          "You do not have a Demat account",
        buttonText: cmpData.loggedInOpenDematButtonText ?? "OPEN DEMAT ACCOUNT",
        onClick: () => {
          if (baseUrl) {
            window.open(baseUrl, redirectNewTab);
          }
        },
      };
    }

    if (hasDemat && !hasPortfolio) {
      const url = isOpenInApp()
        ? cmpData.loggedInBuyStocksUrlApp
        : cmpData.loggedInBuyStocksUrlWeb;
      const redirectNewTab =
        cmpData.loggedInBuyStocksRedirectNewTab === "true" ? "_blank" : "_self";

      return {
        errorMessage:
          cmpData.messages?.loggedInBuyStocksText ??
          cmpData.loggedInBuyStocksText ??
          "You have no stocks in your portfolio",
        buttonText: cmpData.loggedInBuyStocksButtonText ?? "BUY STOCKS",
        onClick: () => {
          if (url) {
            window.open(url, redirectNewTab);
          }
        },
      };
    }

    return undefined;
  }, [cmpData, hasDemat, hasPortfolio]);

  const handleLogin = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isAuthSuccessOrPerpetual) {
        store.dispatch(setIsCommonSsoModalVisible(true));
      }
    },
    [isAuthSuccessOrPerpetual],
  );

  const handleAuthenticate = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      let redirectUrl = isOpenInApp()
        ? cmpData?.urls?.loggedInMFAUrlApp
        : cmpData?.urls?.loggedInMFAUrlWeb;
      if (redirectUrl) {
        if (redirectUrl.includes("<<mobileNumber>>")) {
        try {
          const { mobileNo } = await getSessionData();
            redirectUrl = redirectUrl.replace(/<<mobileNumber>>\s*/g, mobileNo.trim());
          } catch (error) {
            console.error("Error fetching session data:", error);
          }
        }
        handleRedirection(redirectUrl);
      }
    },
    [cmpData?.urls],
  );

  const investments = useMemo(() => {
    const result = getInvestmentData();
    return result;
  }, [getInvestmentData]);
  const hasPortfolioData = investments.length > 0;
  const subHeadingLimit = Number(
    cmpData?.subHeadingTextLimit ?? TITLE_CHAR_LIMIT,
  );
  const shouldShowViewPortfolioLink = stringToBoolean(
    cmpData?.viewPortfolioVariant || "",
  );

  if (!hasPortfolioData) {
    const errorText = handleErrorText();
    return (
      <StockListNotFound
        errorMessage={errorText?.errorMessage ?? API_ERROR_DESC}
        headerErrorMessage={errorText?.buttonText}
        stockNotFoundImgUrl={
          cmpData?.urls?.stockNotFoundImgUrl ??
          "/next-public/static/images/no-stocks-found.svg"
        }
      />
    );
  }

  return (
    <>
      <div className="relative flex w-full max-w-[600px] flex-col overflow-hidden rounded-xl bg-white p-[4px] md:gap-3">
        {(() => {
          if (!isAuthSuccessOrPerpetual) {
            return (
              <AuthenticatePortfolio
                text={cmpData?.loggedOutText ?? ""}
                buttonText={cmpData?.loggedOutButtonText ?? "SIGN IN"}
                onClick={handleLogin}
              />
            );
          }
          if (
            isAuthSuccessOrPerpetual &&
            leadStatus === "ETB" &&
            (code === "" || !code)
          ) {
            return (
              <AuthenticatePortfolio
                text={
                  cmpData?.messages?.loggedInMFAText ??
                  cmpData?.loggedInMFAText ??
                  "For added security, we need to confirm your identity before showing your holdings."
                }
                buttonText={cmpData?.loggedInMFAButtonText ?? "Authenticate"}
                onClick={handleAuthenticate}
              />
            );
          }
          return null;
        })()}
        {/* Header */}
        <div className="pt-[0px] md:pt-[4px]">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-medium text-[#1A1A1A] md:text-base">
              {textEllipsis(cmpData?.subHeading ?? "", subHeadingLimit)}
            </div>
            <button
              type="button"
              onClick={() => setPortfolioVisible(!portfolioVisible)}
              className={`} flex items-center gap-1 text-xs font-medium text-[#FF6700] md:text-sm`}
            >
              <span>
                {portfolioVisible
                  ? cmpData?.actions?.hideBtnText
                  : cmpData?.actions?.showBtnText}
              </span>
              {portfolioVisible ? (
                <i className="icomoon2 text-[18px] before:content-['\e9d1']" />
              ) : (
                <i className="icomoon text-[18px] before:content-['\e971']" />
              )}
            </button>
          </div>

          {/* Investment rows */}
          <div className="flex w-full flex-col gap-2 rounded-lg border border-[#E5E5E5] bg-[#fafafa] px-4 py-3 md:gap-4">
            {[0, 2].map((startIndex) => {
              const left = investments[startIndex];
              const right = investments[startIndex + 1];
              const rightValue = right?.value ?? "0";

              const rawValue = Number((right as any)?.totalPL ?? "0");
              const isPositive = rawValue >= 0;
              const sign = isPositive ? "+" : "-";

              let performanceIcon;

              if (!holdings) {
                performanceIcon = (
                  <i className="icomoon text-[12px] text-green-700 before:content-['\e980']" />
                );
              } else if (isPositive) {
                performanceIcon = (
                  <i className="icomoon text-[12px] text-green-700 before:content-['\e980']" />
                );
              } else {
                performanceIcon = (
                  <i className="icomoon text-[12px] text-[#ff1f00] before:content-['\e981']" />
                );
              }

              return (
                <div
                  className="flex items-center justify-between gap-4"
                  key={startIndex}
                >
                  <div className="flex flex-1 flex-col gap-1">
                    <p className="text-[12px] font-[400px] text-[#666666] md:text-[16px] lg:text-[16px]">
                      {left?.label}
                    </p>
                    <p className="text-[14px] font-medium text-[#1A1A1A] md:text-[18px]">
                      ₹{formatValue(left?.value ?? "")}
                    </p>
                  </div>

                  <div className="flex flex-1 flex-col items-end gap-1">
                    <p className="text-[12px] font-[400px] text-[#666666] md:text-[16px] lg:text-[16px]">
                      {right?.label}
                    </p>
                    <p
                      className={`text-[14px] font-medium md:text-[18px] ${!holdings || isPositive
                          ? "text-green-700"
                          : "text-[#ff1f00]"
                        }`}
                    >
                      {/* Icon color: green when no holdings, otherwise green for positive, red for negative.
                        This applies in both hide and show modes. */}
                      {performanceIcon}
                      {/* Sign is only visible in show mode */}
                      {portfolioVisible && <span className="ml-1">{sign}</span>}
                      ₹{formatValue(rightValue)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        {shouldShowViewPortfolioLink && (
          <button
            type="button"
            className="mb-[0px] inline-flex text-[14px] font-medium text-[#FF6700] md:mb-[10px] md:text-[16px]"
            onClick={handleRedirectionUrl}
          >
            {cmpData?.viewPortfolioText ?? "View Portfolio"}
          </button>
        )}
      </div>
      {(() => {
        if (shouldRenderHoldings && holdings?.holdings) {
          return (
            <section className="pt-2">
              <h2 className="text-[16px] text-base font-medium text-[#1A1A1A]">
                {" "}
                Your Stocks Investments{" "}
              </h2>
              <PortfolioHoldingsComponent data={holdings.holdings} />
              {holdings.showMore && onLoadMore && (
                <button
                  type="button"
                  onClick={onLoadMore}
                  disabled={isLoadingMore}
                  className="mt-4 flex h-10 w-full cursor-pointer items-center justify-center rounded-[23px] border border-[#FF6700] bg-white px-6 pb-[11px] pt-3 text-center text-sm font-medium text-[#FF6700] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-[#FF6700] md:w-[258px] md:hover:bg-[#FF6700] md:hover:text-white md:hover:transition-colors"
                >
                  {isLoadingMore
                    ? "Loading..."
                    : cmpData?.showMoreText || "Show More"}
                </button>
              )}
            </section>
          );
        } else {
          return null;
        }
      })()}
    </>
  );
};

export default PortfolioLockedCard;
